import { useEffect, useState } from 'react';
import { Link, Navigate, useSearchParams } from 'react-router-dom';
import { PageFrame } from '../components/PageFrame';
import { useAuth } from '../contexts/AuthContext';
import { confirmQuestionPackCheckout } from '../lib/checkout';

export function CheckoutCompletePage() {
  const [searchParams] = useSearchParams();
  const sessionId = String(searchParams.get('session_id') || '').trim();
  const { user, loading } = useAuth();
  const [state, setState] = useState<'checking' | 'ready' | 'pending' | 'error'>('checking');
  const [balance, setBalance] = useState<number | null>(null);

  useEffect(() => {
    if (!user || !sessionId) return;
    let active = true;
    let timer: number | null = null;
    let attempts = 0;

    const confirm = async () => {
      attempts += 1;
      try {
        const result = await confirmQuestionPackCheckout(sessionId);
        if (!active) return;
        setBalance(Number(result.questions_remaining || 0));
        if (result.confirmed) {
          setState('ready');
          return;
        }
        if (result.status === 'failed' || result.status === 'expired') {
          setState('error');
          return;
        }
      } catch {
        if (!active) return;
      }
      if (attempts < 8) {
        setState('checking');
        timer = window.setTimeout(() => { void confirm(); }, Math.min(1500 * attempts, 5000));
      } else {
        setState('pending');
      }
    };

    void confirm();
    return () => {
      active = false;
      if (timer !== null) window.clearTimeout(timer);
    };
  }, [sessionId, user]);

  if (!sessionId) return <Navigate to="/pricing" replace />;
  if (!loading && !user) return <Navigate to="/login" replace state={{ from: `/checkout/complete?session_id=${encodeURIComponent(sessionId)}` }} />;

  return (
    <PageFrame>
      <section className="checkout-complete section-shell">
        <p className="eyebrow">Secure checkout</p>
        {state === 'checking' ? <h1>Confirming your payment…</h1> : null}
        {state === 'ready' ? (
          <>
            <h1>Your questions are ready.</h1>
            <p>{balance} questions are available in your private workspace.</p>
            <Link className="button button--warm" to="/workspace">Open workspace</Link>
          </>
        ) : null}
        {state === 'pending' ? (
          <>
            <h1>Your payment is still syncing.</h1>
            <p>You can open the workspace now. The balance will update automatically after Stripe confirms the payment.</p>
            <Link className="button button--ink" to="/workspace">Open workspace</Link>
          </>
        ) : null}
        {state === 'error' ? (
          <>
            <h1>This checkout was not completed.</h1>
            <p>No questions were added. Return to the package page if you would like to try again.</p>
            <Link className="button button--ink" to="/pricing">View question packs</Link>
          </>
        ) : null}
      </section>
    </PageFrame>
  );
}
